import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { Instrument, PrivateAssetData } from './instrument.model';
import { HttpClient } from '@angular/common/http';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';
import { code, country } from 'currency-codes';
import { getContinentName } from '@brixtol/country-continent';
import { getName, getCode } from 'country-list';

export interface InstrumentsState extends EntityState<Instrument, number> {}

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'instrumentsNEW', idKey: 'instrumentId' })
export class InstrumentsStore extends EntityStore<InstrumentsState> {
    constructor(private appConfig: AppConfigQuery, private http: HttpClient) {
        super();
    }

    /**
     * When instruments are added, make sure valuations are populated with ids based on date.
     */
    akitaPreAddEntity(instrument: Instrument) {
        if (instrument?.valuations?.length > 0) {
            return this.sortValuations(extendInstrument(instrument));
        }
        return extendInstrument(instrument);
    }

    /**
     * When instrument is updated, make sure valuations are populated with ids based on date.
     */
    akitaPreUpdateEntity(prevInstrument: Instrument, nextInstrument: Instrument) {
        if (nextInstrument?.valuations?.length > 0) {
            return this.sortValuations(nextInstrument);
        }
        return nextInstrument;
    }

    sortValuations(instrument: Instrument) {
        return {
            ...instrument,
            valuations: instrument.valuations
                .map((valuation) => ({
                    ...valuation,
                    id: valuation.date
                }))
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        };
    }
}

const extendInstrument = (instrument: Instrument): Instrument => {
    let instrumentType: Instrument['instrumentType'] = instrument.type;
    instrument = {
        ...instrument,
        continentName: 'CONTINENT_NOT_DEFINED',
        countryCode: 'COUNTRY_NOT_DEFINED',
        countryName: 'COUNTRY_NOT_DEFINED'
    };
    if (instrument.isin) {
        const countryCode = instrument.isin.substring(0, 2);
        const countryName = getName(countryCode);
        let continentName = 'CONTINENT_NOT_DEFINED';
        try {
            continentName = getContinentName(countryCode);
        } catch (e) {}
        instrument = { ...instrument, countryCode, continentName, countryName };
    }
    switch (instrument.type) {
        case 'BOND':
            return {
                ...instrument,
                assetType: 'ASSET',
                assetClass: 'FIXED_INCOME',
                instrumentType: instrument.type
            };
        case 'CASH':
            return {
                ...instrument,
                // assetType: 'ASSET', depends on if the balance is positive or negative
                assetClass: 'LIQUIDITY',
                instrumentType: instrument.type,
                decoratedInstrumentName: `Cash (${instrument.currency})`,
                ...getCountryByCurrency(instrument.currency)
            };
        case 'COMMERCIAL_REAL_ESTATE':
            return {
                ...instrument,
                assetType: 'ASSET',
                assetClass: 'REAL_ESTATE',
                instrumentType: instrument.type,
                ...getCountryByCurrency(instrument.currency) // TODO: should be determined by location instead of currency
            };
        case 'CONVERTIBLE':
        case 'FXFORWARD':
        case 'FUTURE':
        case 'OPTION':
            return {
                ...instrument,
                assetType: 'ASSET',
                assetClass: 'DERIVATIVE',
                instrumentType: instrument.type
            };
        case 'CRYPTO':
            return {
                ...instrument,
                assetType: 'ASSET',
                assetClass: 'CRYPTO',
                instrumentType: instrument.type
            };
        case 'ETF':
        case 'EQUITY':
        case 'CERT':
        //case 'PRIVATE_EQUITY':
        case 'RIGHT':
        case 'SUBSCRIPTIONRIGHT':
            switch (instrument.type) {
                /*case 'PRIVATE_EQUITY':
                    instrumentType = 'UNLISTED_SHARES';
                        break;*/
                case 'EQUITY':
                    instrumentType = 'SHARES';
                    break;
            }
            // fallback to using currency to determine country if country is not available from ISIN or if it's IE (Ireland) or LU (Luxembourg) https://swimbird.atlassian.net/browse/SWIM-6218
            if (
                instrument.countryCode === 'COUNTRY_NOT_DEFINED' ||
                instrument.countryCode === 'IE' ||
                instrument.countryCode === 'LU' ||
                !instrument.countryName
            ) {
                instrument = {
                    ...instrument,
                    ...getCountryByCurrency(instrument.currency)
                };
            }
            return {
                ...instrument,
                assetType: 'ASSET',
                assetClass: 'EQUITY',
                instrumentType
            };
        case 'PE_SHAREHOLDER_CONTRIBUTION':
        case 'PE_OPTION':
        case 'PRIVATE_EQUITY':
        case 'PE_PREF_SHARE':
        case 'PE_OTHER_SHARE':
        case 'PE_CONVERTIBLE':
            switch (instrument.type) {
                case 'PE_SHAREHOLDER_CONTRIBUTION':
                    instrumentType = 'SHAREHOLDER_CONTRIBUTION';
                    break;
                case 'PE_OPTION':
                    instrumentType = 'OPTION';
                    break;
                case 'PRIVATE_EQUITY':
                    instrumentType = 'UNLISTED_SHARES_ORDINARY';
                    break;
                case 'PE_PREF_SHARE':
                    instrumentType = 'UNLISTED_SHARES_PREFERENCE';
                    break;
                case 'PE_OTHER_SHARE':
                    instrumentType = 'UNLISTED_SHARES_OTHER';
                    break;
                case 'PE_CONVERTIBLE':
                    instrumentType = 'CONVERTIBLE';
                    break;
            }
            return {
                ...instrument,
                assetType: 'ASSET',
                assetClass: 'ASSET_PRIVATE_EQUITY',
                instrumentType
            };
        case 'FUND':
        case 'PRIVATE_FUND':
            // fallback to using currency to determine country if country is not available from ISIN or if it's IE (Ireland) or LU (Luxembourg) https://swimbird.atlassian.net/browse/SWIM-6218
            if (
                instrument.countryCode === 'COUNTRY_NOT_DEFINED' ||
                instrument.countryCode === 'IE' ||
                instrument.countryCode === 'LU' ||
                !instrument.countryName
            ) {
                instrument = {
                    ...instrument,
                    ...getCountryByCurrency(instrument.currency)
                };
            }
            return {
                ...instrument,
                assetType: 'ASSET',
                assetClass: 'FUND',
                instrumentType: instrument.type
            };
        case 'LOAN':
        case 'PE_LOAN':
            return {
                ...instrument,
                assetClass: 'LOAN',
                instrumentType: 'LOAN',
                ...getCountryByCurrency(instrument.currency)
            };
        case 'PRIVATE_ASSET':
            let assetClass;
            let privateAssetType = (instrument as Instrument<PrivateAssetData>)
                .instrumentData.privateAssetType;
            switch (privateAssetType) {
                case 'REAL_ESTATE':
                    assetClass = 'REAL_ESTATE';
                    instrumentType = 'RESIDENTIAL_REAL_ESTATE';
                    break;
                default:
                    instrumentType = privateAssetType;
                    assetClass = 'REAL_ASSET';
                    break;
            }
            return {
                ...instrument,
                assetType: 'ASSET',
                assetClass,
                instrumentType,
                ...getCountryByCurrency(instrument.currency) // TODO: should be determined by location instead of currency
            };

        default:
            return {
                ...instrument,
                assetType: 'ASSET',
                assetClass: 'N/A',
                instrumentType: instrument.type
            };
    }
};

const getCountryByCurrency = (currency: string) => {
    let countryCode = 'COUNTRY_NOT_DEFINED';
    const countriesByCurrency = code(currency).countries;
    let continentName = 'CONTINENT_NOT_DEFINED';
    let countryName = getName(countryCode);

    if (countriesByCurrency.length === 1) {
        countryCode = getCode(countriesByCurrency[0]);
        if (!countryCode) {
            countryCode = 'COUNTRY_NOT_DEFINED';
        } else {
            countryName = getName(countryCode);
        }
    } else if (currency === 'USD') {
        countryCode = 'US';
        countryName = getName(countryCode);
    } else if (currency === 'EUR') {
        countryCode = 'EU';
        continentName = 'Europe';
        countryName = 'Eurozone';
    } else if (currency === 'GBP') {
        countryCode = 'GB';
        countryName = 'United Kingdom'; // since getName(countryCode) returns "United Kingdom of Great Britain and Northern Ireland" which is very long;
    } else if (currency === 'AUD') {
        countryCode = 'AU';
        countryName = getName(countryCode);
    } else if (currency === 'NOK') {
        countryCode = 'NO';
        countryName = getName(countryCode);
    }
    try {
        continentName = getContinentName(countryCode);
    } catch (e) {}
    return {
        countryCode,
        countryName,
        continentName
    };
};
